import { useCallback, useEffect, useMemo, useState } from "react";
import { AppShell, Icon, Page } from "@bot-whatsapp/ui";
import { NAV_ICONS } from "./dashboard/nav-icons";
import { DashboardApiContext, type ApiFactory, type DashboardApi } from "./dashboard/api";
import { createLiveApi } from "./dashboard/live";
import { UnauthorizedContext } from "./dashboard/data";
import { createFixtureApi } from "./dashboard/fixtures";
import { clearToken, readToken, saveToken } from "./dashboard/session";
import { CodesScreen } from "./dashboard/CodesScreen";
import { ConnectionScreen } from "./dashboard/ConnectionScreen";
import { DemografiScreen } from "./dashboard/DemografiScreen";
import { OverviewScreen } from "./dashboard/OverviewScreen";
import { SendersScreen } from "./dashboard/SendersScreen";
import { SubmissionsScreen } from "./dashboard/SubmissionsScreen";
import { TokenScreen } from "./dashboard/TokenScreen";

// The operator dashboard. Direction contract: docs/design/direction.md
// (D3 blueprint_hairline, density default, Geist plus Geist Mono).
//
// Every screen renders inside the JAL Core app-shell in contained scroll mode
// (a dense product screen: the header and the bottom bar stay put, main
// scrolls). Five destinations (the bar law is 3 to 5), hash routed, one
// screen at a time; Koneksi links the bot number (Addendum 2026-10-14).
// The third is labelled "Pairing" (its screen heading stays "Kode Pairing"):
// "Kode Pairing" ellipsized in the label-only bottom bar up to 375px
// (JEV ui.nav_label, confidence 1.0).
const DESTINATIONS = [
  { id: "ringkasan", label: "Ringkasan", href: "#ringkasan" },
  { id: "kiriman", label: "Kiriman", href: "#kiriman" },
  { id: "demografi", label: "Demografi", href: "#demografi" },
  { id: "kode", label: "Pairing", href: "#kode" },
  { id: "pengirim", label: "Pengirim", href: "#pengirim" },
];

type ScreenId = "ringkasan" | "kiriman" | "demografi" | "kode" | "pengirim" | "koneksi";
// Koneksi is a one-time setup screen: it left the bar for Demografi and is
// reached from the header action (JEV ui.nav_label koneksi_to_header, 0.97).
const SCREEN_IDS = new Set<string>([...DESTINATIONS.map((d) => d.id), "koneksi"]);

/** The screen in the hash; else ?view= (an entry link that survives a reload
 *  to the same URL, which the audit tools need); else Ringkasan. */
function readHash(): ScreenId {
  if (typeof location === "undefined") return "ringkasan";
  const hash = location.hash.slice(1);
  if (SCREEN_IDS.has(hash)) return hash as ScreenId;
  const view = new URLSearchParams(location.search).get("view") ?? "";
  return (SCREEN_IDS.has(view) ? view : "ringkasan") as ScreenId;
}

function useCurrentScreen(): ScreenId {
  const [current, setCurrent] = useState<ScreenId>(readHash);
  useEffect(() => {
    const onHash = () => setCurrent(readHash());
    addEventListener("hashchange", onHash);
    return () => removeEventListener("hashchange", onHash);
  }, []);
  return current;
}

/** The one place the data source is chosen: the live client on the typed Hono
 *  RPC, with the bearer on every call. The fixture api is used ONLY when the
 *  URL carries ?demo=1 (so the design audit can render signed-in states with
 *  no backend); session.ts signs that page load in with the fixture token and
 *  never persists it. Fixtures are otherwise for tests. */
function demoRequested(): boolean {
  try {
    return typeof location !== "undefined" && new URLSearchParams(location.search).get("demo") === "1";
  } catch {
    return false;
  }
}

// The CSP enforces Trusted Types, so the glyph SVG is never injected as HTML:
// it goes in as an image, its currentColor baked to the state's ink.
const NAV_INK = { idle: "#474747", active: "#0a5866" };
function navGlyph(glyph: { outline: string; filled: string }, active: boolean): string {
  const body = (active ? glyph.filled : glyph.outline).replaceAll("currentColor", active ? NAV_INK.active : NAV_INK.idle);
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">${body}</svg>`)}`;
}

let fixtureApi: DashboardApi | null = null;
export const createApi: ApiFactory = (token) => {
  if (demoRequested()) return (fixtureApi ??= createFixtureApi());
  return createLiveApi(token);
};

export interface AppProps {
  /** Override the data source (tests). Defaults to createApi. */
  apiFactory?: ApiFactory;
}

export function App({ apiFactory = createApi }: AppProps) {
  const current = useCurrentScreen();
  const [token, setToken] = useState<string | null>(readToken);
  const [expired, setExpired] = useState(false);
  const api = useMemo(() => apiFactory(token ?? ""), [apiFactory, token]);

  const signOut = useCallback(() => {
    clearToken();
    setToken(null);
  }, []);

  const onUnauthorized = useCallback(() => {
    clearToken();
    setExpired(true);
    setToken(null);
  }, []);

  const accept = useCallback((value: string) => {
    saveToken(value);
    setExpired(false);
    setToken(value);
  }, []);

  // Reicon glyphs: Outline when idle, Filled on the current destination.
  const destinations = DESTINATIONS.map((d) => {
    const glyph = NAV_ICONS[d.id];
    if (!glyph) return d;
    return {
      ...d,
      icon: (
        <Icon size={22} color="currentColor">
          <img className="dash-nav-glyph" src={navGlyph(glyph, current === d.id)} alt="" width={22} height={22} draggable={false} />
        </Icon>
      ),
    };
  });

  const screen =
    current === "kiriman" ? (
      <SubmissionsScreen />
    ) : current === "kode" ? (
      <CodesScreen />
    ) : current === "pengirim" ? (
      <SendersScreen />
    ) : current === "koneksi" ? (
      <ConnectionScreen />
    ) : current === "demografi" ? (
      <DemografiScreen />
    ) : (
      <OverviewScreen />
    );

  // Signed out there is nothing to navigate to: the token screen renders its
  // own chrome with no destinations, so no tab reads as current before login.
  if (!token) {
    return (
      <Page direction="D3" rhythm="tight" motion="none">
        <TokenScreen validate={(t) => apiFactory(t).validateToken(t)} onAccepted={accept} expired={expired} />
      </Page>
    );
  }

  return (
    <Page direction="D3" rhythm="tight" motion="none">
      <AppShell
        title="Sistem Data Relawan"
        brand={
          <>
            <span className="dash-brand-full">Sistem Data Relawan</span>
            <span className="dash-brand-short" aria-hidden="true">
              Data Relawan
            </span>
            <span className="dash-brand-tiny" aria-hidden="true">
              Relawan
            </span>
          </>
        }
        destinations={destinations}
        current={current}
        scroll="contained"
        spy={false}
        actions={
          <>
            <a className="btn-ghost dash-signout" href="#koneksi" aria-current={current === "koneksi" ? "page" : undefined}>
              Koneksi
            </a>
            <button type="button" className="btn-ghost dash-signout" onClick={signOut}>
              Keluar
            </button>
          </>
        }
      >
        <DashboardApiContext.Provider value={api}>
          <UnauthorizedContext.Provider value={onUnauthorized}>
            {/* Each screen is its own component, so a screen change remounts it:
                its local state resets and it plays its one quiet entrance
                (motion tier 1, .dash-screen in styles.css). */}
            {screen}
          </UnauthorizedContext.Provider>
        </DashboardApiContext.Provider>
      </AppShell>
    </Page>
  );
}
